import { send } from '@actual-app/core/platform/client/connection';
import { q } from '@actual-app/core/shared/query';

import { aqlQuery } from '#queries/aqlQuery';

export type CategoryRuleSuggestion = {
  payeeId: string;
  payeeName: string;
  categoryId: string;
  categoryName: string;
  support: number;
  total: number;
};
export type HistoryRow = { payee: string | null; category: string };

export function mineCategoryRules(
  rows: HistoryRow[],
  opts: {
    payeeName: Map<string, string>;
    categoryName: Map<string, string>;
    excludePayeeIds: Set<string>;
    minSupport?: number;
    minConsistency?: number;
  },
): CategoryRuleSuggestion[] {
  const minSupport = opts.minSupport ?? 5;
  const minConsistency = opts.minConsistency ?? 0.9;

  // payee -> (category -> count)
  const byPayee = new Map<string, Map<string, number>>();
  for (const r of rows) {
    if (!r.payee || opts.excludePayeeIds.has(r.payee)) continue;
    const cats = byPayee.get(r.payee) ?? new Map<string, number>();
    cats.set(r.category, (cats.get(r.category) ?? 0) + 1);
    byPayee.set(r.payee, cats);
  }

  const out: CategoryRuleSuggestion[] = [];
  for (const [payeeId, cats] of byPayee) {
    let total = 0;
    let bestCat = '';
    let bestCount = 0;
    for (const [cat, count] of cats) {
      total += count;
      if (count > bestCount) {
        bestCount = count;
        bestCat = cat;
      }
    }
    if (total < minSupport || bestCount / total < minConsistency) continue;
    const payeeName = opts.payeeName.get(payeeId);
    const categoryName = opts.categoryName.get(bestCat);
    if (!payeeName || !categoryName) continue;
    out.push({
      payeeId,
      payeeName,
      categoryId: bestCat,
      categoryName,
      support: bestCount,
      total,
    });
  }
  // biggest, most-supported first
  return out.sort((a, b) => b.support - a.support);
}

export type PayeeCleanupSuggestion = {
  variants: string[];
  existing?: string; // resolved payee id by the time it reaches accept
  existingName?: string; // display name (for the modal)
  newName?: string;
  confidence: number;
  guess: boolean;
};

export type RuleSuggestions = {
  unavailable: boolean;
  payeeCleanups: PayeeCleanupSuggestion[];
  categoryRules: CategoryRuleSuggestion[];
};

export async function acceptCategoryRule(
  r: CategoryRuleSuggestion,
): Promise<void> {
  await send('rule-add', {
    stage: null,
    conditionsOp: 'and',
    conditions: [{ op: 'is', field: 'payee', value: r.payeeId }],
    actions: [{ op: 'set', field: 'category', value: r.categoryId }],
  });
}

export async function acceptPayeeCleanup(
  c: PayeeCleanupSuggestion,
): Promise<void> {
  let to = c.existing;
  if (!to && c.newName) {
    to = (await send('payee-create', { name: c.newName })) as unknown as string;
  }
  if (!to) return;
  await send('rule-add-payee-rename', { fromNames: c.variants, to });
}

export async function requestRuleSuggestions(): Promise<RuleSuggestions> {
  const payees = (await send('payees-get')) as Array<{
    id: string;
    name: string;
  }>;
  const payeeName = new Map(payees.map(p => [p.id, p.name]));
  const nameToPayeeId = new Map(
    payees.map(p => [p.name.trim().toLowerCase(), p.id]),
  );

  const categoriesResp = (await send('get-categories')) as {
    list: Array<{ id: string; name: string }>;
  };
  const categoryName = new Map(categoriesResp.list.map(c => [c.id, c.name]));

  // payees already covered by an existing category-setting rule -> skip
  const rules = (await send('rules-get')) as Array<{
    conditions: Array<{ field: string; value: unknown }>;
    actions: Array<{ field?: string }>;
  }>;
  const excludePayeeIds = new Set<string>();
  for (const rule of rules) {
    const setsCategory = rule.actions?.some(a => a.field === 'category');
    if (!setsCategory) continue;
    for (const cond of rule.conditions ?? []) {
      if (cond.field === 'payee' && typeof cond.value === 'string') {
        excludePayeeIds.add(cond.value);
      }
    }
  }

  // category rules | heuristic over categorized history (local, no model)
  const history = (
    await aqlQuery(
      q('transactions')
        .filter({
          category: { $ne: null },
          transfer_id: null,
          is_parent: false,
        })
        .select(['payee', 'category'])
        .limit(5000),
    )
  ).data as Array<{ payee: string | null; category: string }>;
  const categoryRules = mineCategoryRules(history, {
    payeeName,
    categoryName,
    excludePayeeIds,
  });

  // payee cleanups | distinct messy imported_payee strings (with counts)
  const uncats = (
    await aqlQuery(
      q('transactions')
        .filter({ is_parent: false })
        .select(['imported_payee'])
        .limit(20000),
    )
  ).data as Array<{ imported_payee: string | null }>;
  const counts = new Map<string, number>();
  for (const t of uncats) {
    const name = t.imported_payee?.trim();
    if (!name) continue;
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  const messy = [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 200);

  let unavailable = false;
  let payeeCleanups: PayeeCleanupSuggestion[] = [];
  if (messy.length > 0) {
    const raw = (await send('enablebanking-ai-suggest-rules', {
      messy,
      existingPayees: payees.map(p => p.name).filter(Boolean),
    })) as
      | { unavailable?: boolean; clusters?: PayeeCleanupSuggestion[] }
      | { error: string };
    if (!raw || 'error' in raw || raw.unavailable) {
      unavailable = true;
    } else {
      // resolve the LLM's existing-name back to a payee id, keep the name for display
      payeeCleanups = (raw.clusters ?? []).map(c => ({
        ...c,
        existing: c.existing
          ? nameToPayeeId.get(c.existing.trim().toLowerCase())
          : undefined,
        existingName: c.existing || undefined,
      }));
    }
  }

  return { unavailable, payeeCleanups, categoryRules };
}
